/** Hold before mobile augment drag arms (avoids scroll-vs-drag conflicts). */
export const TOUCH_DRAG_HOLD_MS = 150;

/** Finger travel beyond this cancels a pending long-press. */
export const TOUCH_DRAG_MOVE_CANCEL_PX = 10;

/**
 * @param {{ x: number, y: number } | null | undefined} a
 * @param {{ x: number, y: number } | null | undefined} b
 */
export function touchPointDistance(a, b) {
  if (!a || !b) return 0;
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/**
 * @param {{ x: number, y: number } | null | undefined} startPos
 * @param {{ x: number, y: number } | null | undefined} currentPos
 * @param {number} [thresholdPx]
 */
export function shouldCancelTouchDragHold(
  startPos,
  currentPos,
  thresholdPx = TOUCH_DRAG_MOVE_CANCEL_PX
) {
  return touchPointDistance(startPos, currentPos) > thresholdPx;
}

/**
 * @param {{ current: ReturnType<typeof setTimeout> | null }} timerRef
 */
export function clearTouchDragHoldTimer(timerRef) {
  if (timerRef.current != null) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}

/**
 * @param {{ current: ReturnType<typeof setTimeout> | null }} timerRef
 * @param {() => void} onArm
 * @param {number} [holdMs]
 */
export function scheduleTouchDragHold(timerRef, onArm, holdMs = TOUCH_DRAG_HOLD_MS) {
  clearTouchDragHoldTimer(timerRef);
  timerRef.current = setTimeout(() => {
    timerRef.current = null;
    onArm();
  }, holdMs);
}

/**
 * Element under the finger, ignoring the floating drag preview.
 * The preview is centered on the finger, so a plain elementFromPoint hits it
 * and a drop on Match plays never sees the pool.
 * @param {number} clientX
 * @param {number} clientY
 * @returns {Element | null}
 */
/**
 * @param {{ x: number, y: number } | null | undefined} point
 * @param {{ left: number, right: number, top: number, bottom: number } | null | undefined} rect
 */
export function isPointInRect(point, rect) {
  if (!point || !rect) return false;
  return point.x >= rect.left && point.x <= rect.right && point.y >= rect.top && point.y <= rect.bottom;
}

/** Finger is visually over the sticky Match plays bar, regardless of hit-testing. */
export function isPointOverMatchPlays(point) {
  if (!point || typeof document === 'undefined') return false;
  const bar = document.querySelector('.augment-sticky-games');
  if (!bar) return false;
  return isPointInRect(point, bar.getBoundingClientRect());
}

/**
 * Use the last move when it was over Match plays. touchend on iOS can report
 * the match card under the sticky bar instead of where the finger was.
 * @param {{ x: number, y: number } | null | undefined} eventPoint
 * @param {{ x: number, y: number } | null | undefined} lastPoint
 */
export function chooseMatchTouchDropPoint(eventPoint, lastPoint) {
  if (lastPoint && isPointOverMatchPlays(lastPoint)) return lastPoint;
  if (eventPoint && isPointOverMatchPlays(eventPoint)) return eventPoint;
  return eventPoint || lastPoint || null;
}

/**
 * Drop decision for a touch-dragged match play.
 * Over the Match plays bar always removes, even when elementFromPoint
 * returns the match card scrolling underneath that bar.
 * @param {{ point: { x: number, y: number } | null, dragData: { source?: string, sourceGameId?: string } | null, element: Element | null, overMatchPlays: boolean }} args
 */
export function resolveMatchTouchDrop({ point, dragData, element, overMatchPlays }) {
  if (!dragData || !point) return { type: 'cancel' };
  if (dragData.source === 'game' && dragData.sourceGameId && overMatchPlays) {
    return { type: 'remove', gameId: String(dragData.sourceGameId) };
  }
  const gameCard = element?.closest?.('.game-card[data-game-id]');
  const gameId = gameCard?.dataset?.gameId;
  if (gameId) return { type: 'drop', gameId: String(gameId), element: gameCard };
  const pool = element?.closest?.('.augment-sticky-games') || element?.closest?.('.augment-pool');
  if (dragData.source === 'game' && dragData.sourceGameId && pool) {
    return { type: 'remove', gameId: String(dragData.sourceGameId) };
  }
  return { type: 'cancel' };
}

/** A native mouse drag must not cancel a touch drag that is pending or already armed. */
export function shouldIgnoreNativeDragDuringTouch(pending, dragData) {
  return pending != null || dragData != null;
}

/**
 * Claim a drag handle before the gesture starts.
 * React listens for touch on the root with passive: true, so preventDefault there
 * is ignored and iOS cancels the touch as soon as the drag arms (red border flashes off).
 * These listeners are registered up front, non-passive, and turn off the HTML5 drag.
 * @param {Element | null | undefined} element
 */
export function claimTouchDragHandle(element) {
  if (!element || element.dataset.touchDragClaimed === '1') return;
  element.dataset.touchDragClaimed = '1';
  const claim = (event) => {
    // Style picker sits on pool cards; let its taps through without claiming the drag.
    if (event.target?.closest?.('.augment-style-dot, .augment-style-menu')) return;
    element.draggable = false;
    if (event.cancelable) event.preventDefault();
  };
  const releaseNativeDrag = () => {
    element.draggable = true;
  };
  element.addEventListener('touchstart', claim, { passive: false });
  element.addEventListener('touchmove', claim, { passive: false });
  element.addEventListener('touchend', releaseNativeDrag, { passive: true });
  element.addEventListener('touchcancel', releaseNativeDrag, { passive: true });
}

export function claimPoolTouchDrag(element, isDragArmed) {
  if (!element || element.dataset.poolTouchDragClaimed === '1') return;
  element.dataset.poolTouchDragClaimed = '1';
  const armed = typeof isDragArmed === 'function' ? isDragArmed : () => false;
  const scrollsNatively = () =>
    element.classList.contains('augment-card--disabled') || element.classList.contains('augment-card--used');
  element.addEventListener(
    'touchstart',
    () => {
      if (scrollsNatively()) return;
      element.draggable = false;
    },
    { passive: true }
  );
  element.addEventListener(
    'touchmove',
    (event) => {
      if (scrollsNatively() || !armed()) return;
      if (event.cancelable) event.preventDefault();
    },
    { passive: false }
  );
  const releaseNativeDrag = () => {
    element.draggable = true;
  };
  element.addEventListener('touchend', releaseNativeDrag, { passive: true });
  element.addEventListener('touchcancel', releaseNativeDrag, { passive: true });
}

/** Sideways swipe of the Match plays bar once a drag hold has been cancelled. */
export function applyPoolCarouselSwipe(state, x) {
  if (!state?.scroller || typeof x !== 'number') return state ?? null;
  state.scroller.scrollLeft += state.lastX - x;
  return { scroller: state.scroller, lastX: x };
}

export function elementUnderTouch(clientX, clientY) {
  if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return null;
  const ghosts = Array.from(document.querySelectorAll('.touch-drag-ghost'));
  const prevVisibility = ghosts.map((ghost) => ghost.style.visibility);
  ghosts.forEach((ghost) => {
    ghost.style.visibility = 'hidden';
  });
  try {
    const stack =
      typeof document.elementsFromPoint === 'function' ? document.elementsFromPoint(clientX, clientY) : [];
    const visible = stack.filter((el) => el instanceof Element && !el.closest('.touch-drag-ghost'));
    if (visible[0]) return visible[0];
    const single = document.elementFromPoint(clientX, clientY);
    if (!single || single.closest('.touch-drag-ghost')) return null;
    return single;
  } finally {
    ghosts.forEach((ghost, i) => {
      ghost.style.visibility = prevVisibility[i];
    });
  }
}
